/**
 * Custom Report — a saved query over the item list, drawn as a report.
 *
 *   /reports/custom/new        the designer: say or write the search, then save
 *   /reports/custom/:id        a saved report: charts above, the rows below
 *   /reports/custom/:id/print  the print view (PDF)
 *
 * The search, the filters, the columns and the rows are the item list's own
 * block (ItemQueryWorkspace, roadmap N-16/N-19): the same plain-English box,
 * "Fix with AI", autocomplete and English line, with its "Save query" left
 * out — the designer's Save is the one save act here. The block keeps the
 * statement off the address (syncUrl off); a saved report opens with its
 * statement in the bar. The Filters panel is the same query shown as filters;
 * it is not offered while the search cannot be shown as filters. An empty
 * designer offers three starter searches (lib/reportPresets), drawn with the
 * shared StarterCards.
 *
 * The report's columns are its own: seeded from the saved report (or the
 * defaults), saved with it, and never written to the item list's columns.
 *
 * The breakdowns read the same item-field registry the list uses (status is
 * `exit_status`, category keeps every bucket and calls the empty one
 * "Uncategorized"), and the rows are the list's own table with the same owner
 * names. The shared frame (roadmap RP-7) supplies range, segment, export,
 * snapshot and schedule; every bar is a drill into the item list with this
 * report's query plus the bucket.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { BarChart3, FileText, LayoutList, Pencil, Save, X } from 'lucide-react';

import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { useTenantStore } from '../../../stores/prioritization/tenantStore';
import { useUIStore } from '../../../stores/prioritization/uiStore';
import { useEnterpriseIdentityStore } from '../../../stores/prioritization/enterpriseIdentityStore';
import { useUserNameMap } from '../../../hooks/useUserNameMap';
import { useBoundSavedReport } from '../../../hooks/useBoundSavedReport';
import { ReportAudienceField } from '../../../components/prioritization/reports/ReportAudience';
import { CATEGORY_HEX, toneColor } from '../../../components/ds';
import { btnPrimary, btnSecondary } from '../../../components/mono/primitives';
import { StarterCards } from '../../../components/shared/StarterCards';

import { DEFAULT_VISIBLE_COLUMNS, itemListOpeningFilters, type FilterGroup } from '../../../components/prioritization/item-list/hooks/useItemListState';
import { useItemListData, getFieldValue, type ItemRow } from '../../../components/prioritization/item-list/hooks/useItemListData';
import { useItemQueryWorkspace } from '../../../components/prioritization/item-list/hooks/useItemQueryWorkspace';
import {
  ItemQueryBar,
  ItemQueryColumnsButton,
  ItemQueryFiltersButton,
  ItemQueryRows,
  ItemQueryTable,
} from '../../../components/prioritization/item-list/components/ItemQueryWorkspace';
import { useSavedReportsStore } from '../../../stores/prioritization/savedReportsStore';
import { getReportCopies } from '../../../lib/reportDependents';
import { migrateLocalCustomReports } from '../../../lib/migrateLocalCustomReports';
import { getReportPreset, REPORT_STARTERS, type ReportPresetConfig } from '../../../lib/reportPresets';
import { REPORT_REGISTRY } from '../../../lib/reportSettingsService';
import { astToFilterGroups, filterGroupsToAst, printQuery } from '../../../lib/pql';
import { customReportQueryAst } from '../../../lib/customReportQuery';
import { audienceSentence, type ReportVisibility, type SavedReport } from '../../../types/prioritization/savedReports';
import type { ReportSnapshotSummary } from '../../../lib/reportSnapshotsService';

import { ReportFrame } from '../../../components/prioritization/reports/shared/ReportFrame';
import { ReportChartCard } from '../../../components/prioritization/reports/shared/ReportChartCard';
import { ReportBreakdownChart, type BreakdownDatum } from '../../../components/prioritization/reports/shared/ReportBreakdownChart';
import { clause, joinClauses, itemsHrefForStatement } from '../../../components/prioritization/reports/shared/drillToItems';
import { rowInRange } from '../../../components/prioritization/reports/shared/reportRange';
import { bucketBySegment, segmentDefinition, titleCase, type SegmentNames } from '../../../components/prioritization/reports/shared/reportSegment';
import { useReportControls, type ReportControls } from '../../../components/prioritization/reports/shared/useReportControls';
import { ReportPrintShell } from './ReportPrintShell';

const STATUS_ORDER = ['active', 'deferred', 'archived'] as const;
const STATUS_LABEL: Record<string, string> = { active: 'Active', deferred: 'Deferred', archived: 'Archived' };

// ============================================================================
// Breakdowns — status, category, segment — from the same rows as the table
// ============================================================================

interface Breakdowns {
  status: BreakdownDatum[];
  category: BreakdownDatum[];
  segment: BreakdownDatum[];
}

function useBreakdowns(rows: ItemRow[], statement: string, controls: ReportControls): Breakdowns & { summary: () => ReportSnapshotSummary } {
  const orgUnits = useEnterpriseIdentityStore((s) => s.orgUnits);
  const segDef = segmentDefinition(controls.segment);
  const segmentPeopleIds = useMemo(
    () => (segDef?.key === 'owner' ? rows.map((r) => String(getFieldValue(r, 'ownerId') ?? '')).filter(Boolean) : []),
    [rows, segDef],
  );
  const people = useUserNameMap(segmentPeopleIds);
  const names: SegmentNames = useMemo(() => ({ orgUnits, people }), [orgUnits, people]);

  return useMemo(() => {
    const base = [statement || null, controls.resolved.pql];
    const drill = (c: string) => itemsHrefForStatement(joinClauses([...base, c]));

    const statusCounts: Record<string, number> = {};
    const categoryCounts: Record<string, number> = {};
    for (const r of rows) {
      const st = String(getFieldValue(r, 'exit_status') ?? 'active');
      statusCounts[st] = (statusCounts[st] ?? 0) + 1;
      const cat = (r.itemCategory as string | undefined) ?? '';
      categoryCounts[cat] = (categoryCounts[cat] ?? 0) + 1;
    }
    const tone: Record<string, string> = { active: toneColor('slate'), deferred: toneColor('mustard'), archived: toneColor('neutral') };
    const status: BreakdownDatum[] = STATUS_ORDER.filter((s) => statusCounts[s]).map((s) => ({
      key: s, label: STATUS_LABEL[s], count: statusCounts[s], color: tone[s], href: drill(clause('status', s)),
    }));
    const category: BreakdownDatum[] = Object.entries(categoryCounts)
      .map(([cat, count]) => ({
        key: cat || null,
        label: cat ? titleCase(cat) : 'Uncategorized',
        count,
        color: cat ? (CATEGORY_HEX[cat] ?? toneColor('neutral')) : toneColor('neutral'),
        href: drill(clause('category', cat || null)),
      }))
      .sort((a, b) => b.count - a.count);
    const segment: BreakdownDatum[] = segDef
      ? bucketBySegment(rows, segDef, names).map((b) => ({
          key: b.value, label: b.label, count: b.rows.length, color: toneColor('slate'), href: drill(clause(segDef.pqlField, b.value)),
        }))
      : [];
    const toRows = (data: BreakdownDatum[]) => data.map((d) => ({ label: d.label, value: d.count, href: d.href, color: d.color }));
    const summary = (): ReportSnapshotSummary => ({
      stats: [
        { label: 'Items', value: rows.length },
        { label: 'Active', value: statusCounts.active ?? 0 },
        { label: 'Deferred', value: statusCounts.deferred ?? 0 },
        { label: 'Archived', value: statusCounts.archived ?? 0 },
      ],
      series: [
        { title: 'By status', rows: toRows(status) },
        { title: 'By category', rows: toRows(category) },
        ...(segDef ? [{ title: `By ${segDef.label.toLowerCase()}`, rows: toRows(segment) }] : []),
      ],
    });
    return { status, category, segment, summary };
  }, [rows, statement, controls.resolved.pql, segDef, names]);
}

function BreakdownCharts({ b, segmentLabel, print = false }: { b: Breakdowns; segmentLabel: string | null; print?: boolean }) {
  return (
    <div className={`grid gap-4 ${print ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
      <ReportChartCard title="By status" description="Click a bar for those items." print={print}>
        <ReportBreakdownChart data={b.status} orientation="columns" height={200} print={print} />
      </ReportChartCard>
      <ReportChartCard title="By category" description="Every bucket, the empty one included. Click a bar for those items." print={print}>
        <ReportBreakdownChart data={b.category} orientation="bars" print={print} />
      </ReportChartCard>
      {segmentLabel && (
        <ReportChartCard title={`By ${segmentLabel.toLowerCase()}`} description="Click a bar for those items." print={print} className="md:col-span-2">
          <ReportBreakdownChart data={b.segment} orientation="bars" print={print} />
        </ReportChartCard>
      )}
    </div>
  );
}

// ============================================================================
// Save modal
// ============================================================================

function SaveReportModal({
  existing,
  onSave,
  onClose,
}: {
  existing?: SavedReport;
  onSave: (name: string, description: string, visibility: ReportVisibility) => void;
  onClose: () => void;
}) {
  const [name, setName] = useState(existing?.name ?? '');
  const [desc, setDesc] = useState(existing?.description ?? '');
  const [visibility, setVisibility] = useState<ReportVisibility>(existing?.visibility ?? 'workspace');
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative bg-[var(--bg-raised)] shadow-xl max-w-sm w-full p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-[var(--text-pri)]">{existing ? 'Update Report' : 'Save Report'}</h3>
          <button type="button" onClick={onClose} className="text-[var(--text-ter)] hover:text-[var(--text-sec)]">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Name</label>
            <input
              autoFocus
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Triaged items by category"
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[var(--text-sec)] mb-1">Description (optional)</label>
            <input
              type="text"
              value={desc}
              onChange={(e) => setDesc(e.target.value)}
              placeholder="What does this report show?"
              className="bg-[var(--bg-raised)] text-[var(--text-pri)] w-full border border-[var(--rule)] px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
            />
          </div>
          <ReportAudienceField report={existing} value={visibility} onChange={setVisibility} />
        </div>
        <div className="mt-5 flex justify-end gap-3">
          <button type="button" {...btnSecondary()} onClick={onClose}>Cancel</button>
          <button type="button" {...btnPrimary()} disabled={!name.trim()} onClick={() => onSave(name.trim(), desc.trim(), visibility)}>
            {existing ? 'Update' : 'Save Report'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// The statement a report opens with
// ============================================================================

/** A new report's opening statement: a preset's filters and sort, or else the given (default) filters. */
function openingStatementFor(preset: ReportPresetConfig | undefined, defaultGroups: FilterGroup[]): string {
  const groups: FilterGroup[] = preset
    ? (preset.filterGroups ?? []).map((g, gi) => ({
        id: `preset-${gi}`,
        conjunction: g.conjunction,
        rules: g.rules.map((r, ri) => ({ ...r, id: `preset-${gi}-${ri}` })),
      }))
    : defaultGroups;
  return printQuery({ where: filterGroupsToAst(groups), orderBy: preset?.sort ?? [] });
}

/** A saved report's statement: the one it was saved with, or its filters printed as one. */
function savedStatementText(report: SavedReport): string {
  if (report.queryText) return report.queryText;
  const ast = report.queryAst ?? filterGroupsToAst(report.filterGroups as FilterGroup[]);
  return ast ? printQuery({ where: ast, orderBy: [] }) : '';
}

// ============================================================================
// Main
// ============================================================================

export function CustomReportPage() {
  const { id: reportId } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const presetKey = searchParams.get('preset');
  const navigate = useNavigate();
  const items = useItemsStore((s) => s.items);
  const fetchFromSupabase = useItemsStore((s) => s.fetchFromSupabase);
  const currentWorkspaceId = useTenantStore((s) => s.currentWorkspaceId) ?? 'default';
  const addNotification = useUIStore((s) => s.addNotification);
  const controls = useReportControls();

  const isNew = reportId === 'new' || !reportId;

  useEffect(() => { fetchFromSupabase(); }, [fetchFromSupabase]);

  // Saved reports (Supabase-backed). Fetch, then import any legacy localStorage
  // reports (once) so deep links to migrated reports resolve.
  const savedReports = useSavedReportsStore((s) => s.reports);
  const fetchReports = useSavedReportsStore((s) => s.fetchFromSupabase);
  const createReportPersisted = useSavedReportsStore((s) => s.createReportPersisted);
  const updateReport = useSavedReportsStore((s) => s.updateReport);
  const setReportVisibility = useSavedReportsStore((s) => s.setReportVisibility);
  const libraryError = useSavedReportsStore((s) => s.error);
  const [libraryLoaded, setLibraryLoaded] = useState(false);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      await fetchReports();
      if (cancelled) return;
      migrateLocalCustomReports(currentWorkspaceId);
      setLibraryLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [fetchReports, currentWorkspaceId]);

  const activeReport = isNew ? undefined : savedReports.find((r) => r.id === reportId);

  // Export is gated on analytics.export inside ReportFrame, for every report.

  // Title editing
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState('');

  // Section visibility
  const [showGraphical, setShowGraphical] = useState(true);
  const [showTabular, setShowTabular] = useState(true);

  const [showSaveModal, setShowSaveModal] = useState(false);

  // The report's own columns — not the item list's (those are the person's,
  // kept in this browser). Seeded from the saved report below, saved with it.
  const [visibleColumns, setVisibleColumns] = useState<string[]>(() => [...DEFAULT_VISIBLE_COLUMNS]);

  // The search, the filters, the columns and the rows: the item list's block.
  // A new report opens with a statement already in the bar: a preset's
  // filters and sort, or the list's default filters. A saved report's
  // statement is put in below once it loads. The report's range narrows the
  // query's rows (RP-7); grouped rows keep the query's grouping.
  const [openingStatement] = useState<string | undefined>(() =>
    isNew ? openingStatementFor(getReportPreset(presetKey), itemListOpeningFilters()) : undefined,
  );
  const keepRow = useCallback((r: ItemRow) => rowInRange(r, controls.resolved), [controls.resolved]);
  const ws = useItemQueryWorkspace({
    items,
    syncUrl: false,
    initialStatement: openingStatement,
    columns: { visible: visibleColumns, setVisible: setVisibleColumns },
    keepRow,
  });
  const {
    searchQuery,
    sort, setSort,
    groupBy, setGroupBy,
    toggleCluster,
    allColumns,
    statement: statementQuery,
    rows,
    totalCount,
  } = ws;
  const { applyStatement, appliedQuery, canonicalText, mode: statementMode } = statementQuery;

  // Owner names through the shared lookup — the same one the item list uses.
  const ownerNameMap = useUserNameMap(rows.map((r) => r.ownerId));

  // Load a saved report into the bar once it has loaded (a deep link arrives
  // before the library does), and again when another report is opened here.
  useEffect(() => {
    if (!activeReport) return;
    setSort(activeReport.sort);
    setGroupBy(activeReport.groupBy);
    setVisibleColumns(activeReport.visibleColumns?.length ? activeReport.visibleColumns : [...DEFAULT_VISIBLE_COLUMNS]);
    applyStatement(savedStatementText(activeReport));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeReport?.id]);

  // A preset seeds its grouping (its filters and sort are in the opening statement).
  useEffect(() => {
    if (!isNew || !presetKey) return;
    const preset = getReportPreset(presetKey);
    if (preset?.groupBy !== undefined) setGroupBy(preset.groupBy);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presetKey, isNew]);

  // A statement that does not parse is not saved: the save would store the
  // last search that did, which is not the one on screen.
  const searchUnsaved = statementMode === 'statement' && statementQuery.error !== null;

  // What a save writes: the statement as the bar applies it, in either mode.
  const savedQuery = useMemo(() => ({
    queryText: canonicalText || undefined,
    queryAst: appliedQuery.where,
    filterGroups: astToFilterGroups(appliedQuery.where) ?? [],
    sort: appliedQuery.orderBy.length > 0 ? appliedQuery.orderBy : sort,
  }), [canonicalText, appliedQuery, sort]);

  // Save / update
  const handleSaveReport = useCallback(async (name: string, description: string, visibility: ReportVisibility) => {
    if (searchUnsaved) return;
    if (activeReport) {
      updateReport(activeReport.id, { name, description, ...savedQuery, groupBy, visibleColumns });
      setShowSaveModal(false);
      if (visibility !== (activeReport.visibility ?? 'workspace')) {
        const { error } = await setReportVisibility(activeReport.id, visibility);
        addNotification(error
          ? { type: 'error', title: 'Who can see it did not change', message: error, duration: 6000 }
          : { type: 'success', title: 'Report saved', message: `"${name}" saved. ${audienceSentence(visibility)}`, duration: 4000 });
        return;
      }
      // Rules copied from this query keep their own text (migration 725) — an
      // edit here does not reach them, so don't let the save imply it did.
      const copies = getReportCopies(activeReport.id);
      addNotification({
        type: 'success',
        title: 'Report saved',
        message: copies.length > 0
          ? `"${name}" saved. ${copies.length} rule${copies.length === 1 ? '' : 's'} copied from it (${copies.map((c) => c.name).join(', ')}) keep their own text and are unchanged.`
          : `"${name}" saved.`,
        duration: copies.length > 0 ? 7000 : 3000,
      });
    } else {
      const { report: created, error } = await createReportPersisted({
        name,
        description,
        folderId: null,
        ...savedQuery,
        groupBy,
        visibleColumns,
        activeChart: 'category',
        visibility,
      });
      setShowSaveModal(false);
      if (!created) {
        addNotification({ type: 'error', title: 'Report not saved', message: error ?? 'The report could not be saved.', duration: 6000 });
        return;
      }
      addNotification({ type: 'success', title: 'Report saved', message: `"${name}" saved. ${audienceSentence(visibility)}`, duration: 4000 });
      navigate(`/prioritization/reports/custom/${created.id}${controls.search ? `?${controls.search}` : ''}`, { replace: true });
    }
  }, [searchUnsaved, activeReport, savedQuery, groupBy, visibleColumns, createReportPersisted, updateReport, setReportVisibility, addNotification, navigate, controls.search]);

  // CSV rows — columns-driven: exactly the columns the report shows, with labels
  // + values resolved through the shared item-field registry (getFieldValue).
  const csvRows = useCallback((): Record<string, unknown>[] => {
    const cols = visibleColumns.length > 0 ? visibleColumns : DEFAULT_VISIBLE_COLUMNS;
    const labelOf = (key: string) => allColumns.find((c) => c.key === key)?.label ?? key;
    const format = (v: unknown): string => {
      if (v === null || v === undefined) return '';
      if (Array.isArray(v)) return v.join('; ');
      if (v instanceof Date) return v.toISOString();
      return String(v);
    };
    return rows.map((r) => Object.fromEntries(cols.map((c) => [labelOf(c), format(c === 'owner' ? (ownerNameMap[r.ownerId ?? ''] ?? getFieldValue(r, c)) : getFieldValue(r, c))])));
  }, [rows, visibleColumns, allColumns, ownerNameMap]);

  // The query as a statement without its order — what a drill carries and what a snapshot records.
  const statement = useMemo(() => (appliedQuery.where ? printQuery({ where: appliedQuery.where, orderBy: [] }) : ''), [appliedQuery]);
  const showStarters = isNew && !presetKey && statementMode === 'statement' && statementQuery.text === openingStatement;

  const breakdowns = useBreakdowns(rows, statement, controls);
  const segmentLabel = segmentDefinition(controls.segment)?.label ?? null;

  const presetReport = presetKey ? REPORT_REGISTRY.find((r) => r.key === presetKey) : undefined;
  const reportName = activeReport?.name ?? presetReport?.name ?? (isNew ? 'New Report' : 'Report');
  const reportPath = activeReport ? `/prioritization/reports/custom/${activeReport.id}` : '/prioritization/reports/custom/new';

  const titleSlot = editingTitle ? (
    <span className="flex items-center gap-2">
      <input
        autoFocus
        type="text"
        value={titleDraft}
        onChange={(e) => setTitleDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            if (activeReport && titleDraft.trim()) updateReport(activeReport.id, { name: titleDraft.trim() });
            setEditingTitle(false);
          }
          if (e.key === 'Escape') setEditingTitle(false);
        }}
        className="bg-[var(--bg-raised)] border border-[var(--rule)] px-2 py-0.5 text-lg min-w-[260px]"
        style={{ fontFamily: 'var(--serif)' }}
      />
      <button type="button" onClick={() => setEditingTitle(false)} className="p-0.5 text-[var(--text-ter)] hover:text-[var(--text-sec)]" aria-label="Stop editing the name">
        <X className="h-4 w-4" />
      </button>
    </span>
  ) : (
    <h1
      className="group flex cursor-pointer items-center gap-2 text-[1.75rem] leading-tight text-[var(--text-pri)]"
      style={{ fontFamily: 'var(--serif)', fontWeight: 400 }}
      onClick={() => { if (activeReport) { setTitleDraft(activeReport.name); setEditingTitle(true); } }}
      title={activeReport ? 'Rename' : undefined}
    >
      {reportName}
      {activeReport && <Pencil className="h-3.5 w-3.5 text-[var(--text-ter)] opacity-0 transition-opacity group-hover:opacity-100" />}
    </h1>
  );

  // Nothing at this address for this person: deleted, or kept to the person who
  // saved it. The row rules decide that; the page does not say which.
  if (!isNew && libraryLoaded && !libraryError && !activeReport) {
    return (
      <div className="py-16 text-center" data-testid="report-not-here">
        <p className="text-sm font-medium text-[var(--text-sec)]">No saved report at this address.</p>
        <p className="mt-1 text-xs text-[var(--text-ter)]">It may have been deleted, or the person who saved it may have kept it to themselves.</p>
        <Link to="/prioritization/reports/custom" className="mt-4 inline-block text-sm text-[var(--text-sec)] underline underline-offset-2">
          All saved reports
        </Link>
      </div>
    );
  }

  return (
    <>
      <ReportFrame
        reportKey={activeReport ? `custom:${activeReport.id}` : 'custom:new'}
        reportPath={reportPath}
        name={reportName}
        question={reportName}
        titleSlot={titleSlot}
        description={activeReport?.description ?? presetReport?.description ?? (isNew ? 'Say or write what the report should show in the box below, then save it.' : undefined)}
        controls={controls}
        rangeNote="Windows count items by created date."
        csv={{ filename: `report-${activeReport?.id ?? 'new'}`, rows: csvRows }}
        summary={breakdowns.summary}
        unavailableReason={isNew ? 'Save the report first; then it can be printed, saved as a snapshot, or scheduled.' : null}
        statement={statement || null}
        savedReportId={activeReport?.id ?? null}
        justMe={activeReport?.visibility === 'private'}
        controlsExtra={ws.filtersOffered ? <ItemQueryFiltersButton ws={ws} /> : undefined}
        actionsExtra={
          <button
            type="button"
            {...btnPrimary()}
            disabled={searchUnsaved}
            title={searchUnsaved ? 'Fix the search in the box first; the report saves the search you see.' : undefined}
            onClick={() => setShowSaveModal(true)}
          >
            <Save className="h-3.5 w-3.5" />
            {activeReport ? 'Update' : 'Save'}
          </button>
        }
      >
        <div className="border border-[var(--rule)]">
          <ItemQueryBar
            ws={ws}
            view={{ sort, groupBy, visibleColumns, activeChart: 'category' }}
            appliedReport={activeReport?.queryText ? { id: activeReport.id, queryText: activeReport.queryText } : null}
            saveVerb={false}
            openAskAiFirst={isNew && !presetKey}
          />
        </div>

        {showStarters && (
          <StarterCards
            label="Example reports"
            lead="Or start from one of these, and change any part of it before you save."
            useLabel="Use this search"
            columns={3}
            cardTestId="report-starter"
            cards={REPORT_STARTERS.map((starter) => ({
              key: starter.sentence,
              sentence: starter.sentence,
              onUse: () => applyStatement(starter.statement),
            }))}
          />
        )}

        <ItemQueryRows ws={ws} className="flex gap-4">
          <div className="min-w-0 flex-1 space-y-4">
            {/* Graphical section */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-[var(--text-ter)]" />
                <span className="text-sm font-semibold text-[var(--text-sec)]">Charts</span>
                <span className="text-xs text-[var(--text-ter)]">
                  {rows.length.toLocaleString()} items{rows.length !== totalCount ? ` of ${totalCount.toLocaleString()}` : ''}
                </span>
              </div>
              <button type="button" {...btnSecondary('h-7 px-2 text-[0.65rem]')} onClick={() => setShowGraphical((v) => !v)}>
                {showGraphical ? 'Hide' : 'Show'}
              </button>
            </div>
            {showGraphical && <BreakdownCharts b={breakdowns} segmentLabel={segmentLabel} />}

            {/* Tabular section */}
            <div className="overflow-hidden border border-[var(--rule)] bg-[var(--bg-raised)]">
              <div className="flex items-center justify-between border-b border-[var(--rule)] px-5 py-3">
                <div className="flex items-center gap-2">
                  <LayoutList className="h-4 w-4 text-[var(--text-ter)]" />
                  <span className="text-sm font-semibold text-[var(--text-sec)]">Rows</span>
                  {rows.length > 0 && <span className="text-xs text-[var(--text-ter)]">({rows.length} items)</span>}
                </div>
                <div className="flex items-center gap-2">
                  {showTabular && <ItemQueryColumnsButton ws={ws} />}
                  <button type="button" {...btnSecondary('h-7 px-2 text-[0.65rem]')} onClick={() => setShowTabular((v) => !v)}>
                    {showTabular ? 'Hide' : 'Show'}
                  </button>
                </div>
              </div>

              {showTabular && (
                rows.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-16 text-[var(--text-ter)]">
                    <FileText className="mb-3 h-10 w-10 text-[var(--text-ter)]" />
                    <p className="text-sm font-medium text-[var(--text-sec)]">
                      {statement || searchQuery ? 'No items match this search' : 'No items in this range'}
                    </p>
                    <p className="mt-1 text-xs">
                      {statement ? 'Change the search above, or widen the range.' : 'Widen the range.'}
                    </p>
                  </div>
                ) : (
                  <ItemQueryTable
                    ws={ws}
                    onRowClick={(item: ItemRow) => {
                      // A cluster entry has no detail page of its own — it expands.
                      if (item.clusterEntry) toggleCluster(item.clusterEntry.groupId);
                      else navigate(`/prioritization/items/${item.id}`);
                    }}
                    ownerNameMap={ownerNameMap}
                  />
                )
              )}
            </div>
          </div>
        </ItemQueryRows>
      </ReportFrame>

      {showSaveModal && (
        <SaveReportModal
          existing={activeReport}
          onSave={(name, description, visibility) => void handleSaveReport(name, description, visibility)}
          onClose={() => setShowSaveModal(false)}
        />
      )}
    </>
  );
}

// ============================================================================
// Print view — the saved report's charts and rows, for the browser's PDF
// ============================================================================

export function CustomReportPrintPage() {
  const { id } = useParams<{ id: string }>();
  const controls = useReportControls();
  const items = useItemsStore((s) => s.items);
  const fetchFromSupabase = useItemsStore((s) => s.fetchFromSupabase);
  useEffect(() => { fetchFromSupabase(); }, [fetchFromSupabase]);
  const { report, loading } = useBoundSavedReport(id);

  const { rows: queryRows } = useItemListData(
    items, '', (report?.filterGroups ?? []) as FilterGroup[], report?.sort ?? [{ field: 'updatedAt', direction: 'desc' }], null, customReportQueryAst(null, report)
  );
  const rows = useMemo(() => queryRows.filter((r) => !r.clusterEntry && rowInRange(r, controls.resolved)), [queryRows, controls.resolved]);
  const ownerNameMap = useUserNameMap(rows.map((r) => r.ownerId));
  const statement = useMemo(() => {
    if (!report) return '';
    const ast = report.queryAst ?? filterGroupsToAst(report.filterGroups as FilterGroup[]);
    return report.queryText ?? (ast ? printQuery({ where: ast, orderBy: [] }) : '');
  }, [report]);
  const breakdowns = useBreakdowns(rows, statement, controls);
  const segmentLabel = segmentDefinition(controls.segment)?.label ?? null;

  const name = report?.name ?? (loading ? 'Loading…' : 'Report');
  const backHref = `/prioritization/reports/custom/${id ?? ''}${controls.search ? `?${controls.search}` : ''}`;

  return (
    <ReportPrintShell
      title={name}
      backHref={backHref}
      meta={`${report?.description ? `${report.description} · ` : ''}${controls.resolved.label}${segmentLabel ? ` · by ${segmentLabel}` : ''} · ${new Date().toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}`}
    >
      {!report ? (
        <p className="text-sm text-[var(--text-ter)]">{loading ? 'Loading the report…' : 'No saved report at this address.'}</p>
      ) : (
        <div className="space-y-6">
          {statement && <p className="font-mono text-xs text-[var(--text-ter)]">Query: {statement}</p>}
          <BreakdownCharts b={breakdowns} segmentLabel={segmentLabel} print />
          <table className="w-full text-sm">
            <tbody>
              {rows.slice(0, 60).map((r) => (
                <tr key={r.id} className="border-b border-[var(--rule)]">
                  <td className="py-1.5 pr-3 text-[var(--text-pri)]">{r.title}</td>
                  <td className="py-1.5 pr-3 text-[var(--text-ter)]">{STATUS_LABEL[String(getFieldValue(r, 'exit_status') ?? 'active')] ?? 'Active'}</td>
                  <td className="py-1.5 pr-3 text-[var(--text-ter)]">{r.itemCategory ? titleCase(String(r.itemCategory)) : '—'}</td>
                  <td className="py-1.5 text-[var(--text-ter)]">{ownerNameMap[r.ownerId ?? ''] ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {rows.length > 60 && <p className="text-xs text-[var(--text-ter)]">Showing the first 60 of {rows.length} rows. Export CSV for every row.</p>}
        </div>
      )}
    </ReportPrintShell>
  );
}

export default CustomReportPage;
